import { useState, useCallback, useRef, Children, useEffect } from 'react';
import { KEYS } from '../navigation.constants';
import { useFocusItem } from '../use-focus-item';

export function useGridNavigation({
  children,
  columns: staticColumns = null,
  dynamicColumns: isDynamic = false,
  itemWidth = null,
  loop = true,
  disabled = false,
  onItemActivate = null,
  onItemFocus = null,
  jumpRowCount = 3,
}) {
  const [focusIndex, setFocusIndex] = useState(0);
  const [calculatedColumns, setCalculatedColumns] = useState(staticColumns || 1);
  const containerRef = useRef(null);
  const items = Children.toArray(children);
  const itemCount = items.length;

  // Calculate columns dynamically based on container width
  useEffect(() => {
    if (!isDynamic && staticColumns) {
      setCalculatedColumns(staticColumns);
      return;
    }

    const container = containerRef.current;
    if (!container) return;

    const calculateColumns = () => {
      const containerWidth = container.offsetWidth;
      if (itemWidth && containerWidth) {
        const cols = Math.max(1, Math.floor(containerWidth / itemWidth));
        setCalculatedColumns(cols);
      } else if (container.firstElementChild) {
        // Calculate based on first item's width
        const firstItem = container.firstElementChild;
        const itemWidthWithGap =
          firstItem.offsetWidth + parseInt(getComputedStyle(container).gap || '0', 10);
        if (itemWidthWithGap > 0) {
          const cols = Math.max(1, Math.floor(containerWidth / itemWidthWithGap));
          setCalculatedColumns(cols);
        }
      }
    };

    calculateColumns();

    const resizeObserver = new ResizeObserver(calculateColumns);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [isDynamic, staticColumns, itemWidth, itemCount]);

  const columns = calculatedColumns;
  const rows = Math.ceil(itemCount / columns);

  const { focusItem, getCurrentFocusIndex } = useFocusItem({
    containerRef,
    itemCount,
    items,
    setFocusIndex,
    focusIndex,
    onItemFocus,
  });

  const getNextIndex = useCallback(
    (currentIndex, key) => {
      if (itemCount === 0) return 0;

      const currentRow = Math.floor(currentIndex / columns);
      const currentCol = currentIndex % columns;
      let nextIndex = currentIndex;

      switch (key) {
        case KEYS.ARROW_RIGHT:
          if (currentCol < columns - 1 && currentIndex + 1 < itemCount) {
            nextIndex = currentIndex + 1;
          } else if (loop) {
            // Move to first item of next row or wrap to first item
            nextIndex = currentRow < rows - 1 ? (currentRow + 1) * columns : 0;
          }
          break;

        case KEYS.ARROW_LEFT:
          if (currentCol > 0) {
            nextIndex = currentIndex - 1;
          } else if (loop) {
            // Move to last item of previous row or wrap to last item
            if (currentRow > 0) {
              const previousRowEnd = currentRow * columns - 1;
              nextIndex = previousRowEnd;
            } else {
              nextIndex = itemCount - 1;
            }
          }
          break;

        case KEYS.ARROW_DOWN:
          const nextRowIndex = currentIndex + columns;
          if (nextRowIndex < itemCount) {
            nextIndex = nextRowIndex;
          } else if (loop) {
            // Wrap to the same column in the first row
            nextIndex = currentCol;
            // Ensure we don't exceed item count
            if (nextIndex >= itemCount) {
              nextIndex = itemCount - 1;
            }
          }
          break;

        case KEYS.ARROW_UP:
          const prevRowIndex = currentIndex - columns;
          if (prevRowIndex >= 0) {
            nextIndex = prevRowIndex;
          } else if (loop) {
            // Wrap to the same column in the last row
            const lastRowStart = (rows - 1) * columns;
            nextIndex = lastRowStart + currentCol;
            // Ensure we don't exceed item count
            if (nextIndex >= itemCount) {
              // Move to last item if calculated position doesn't exist
              nextIndex = itemCount - 1;
            }
          }
          break;

        case KEYS.HOME:
          nextIndex = 0;
          break;

        case KEYS.END:
          nextIndex = itemCount - 1;
          break;

        case KEYS.PAGE_DOWN:
          // Move down by multiple rows
          const pageDownIndex = Math.min(currentIndex + columns * jumpRowCount, itemCount - 1);
          nextIndex = pageDownIndex;
          break;

        case KEYS.PAGE_UP:
          // Move up by multiple rows
          const pageUpIndex = Math.max(currentIndex - columns * jumpRowCount, 0);
          nextIndex = pageUpIndex;
          break;

        default:
          break;
      }

      return nextIndex;
    },
    [itemCount, columns, rows, loop, jumpRowCount],
  );

  const handleKeyDown = useCallback(
    event => {
      if (disabled || itemCount === 0) return;

      const { key } = event;
      const currentIndex = getCurrentFocusIndex();
      let handled = false;
      let nextIndex = currentIndex;

      switch (key) {
        case KEYS.ARROW_UP:
        case KEYS.ARROW_DOWN:
        case KEYS.ARROW_LEFT:
        case KEYS.ARROW_RIGHT:
        case KEYS.HOME:
        case KEYS.END:
        case KEYS.PAGE_UP:
        case KEYS.PAGE_DOWN:
          nextIndex = getNextIndex(currentIndex, key);
          handled = true;
          break;

        case KEYS.ENTER:
        case KEYS.SPACE:
          if (onItemActivate && items[currentIndex]) {
            event.preventDefault();
            event.stopPropagation();
            onItemActivate(items[currentIndex], currentIndex);
            handled = true;
          }
          break;

        default:
          break;
      }

      if (handled && nextIndex !== currentIndex) {
        event.preventDefault();
        event.stopPropagation();
        focusItem(nextIndex);
      } else if (handled) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [disabled, itemCount, getCurrentFocusIndex, getNextIndex, focusItem, onItemActivate, items],
  );

  const handleFocus = useCallback(
    index => {
      if (disabled) return;
      setFocusIndex(index);
      if (onItemFocus) {
        onItemFocus(items[index], index);
      }
    },
    [disabled, items, onItemFocus],
  );

  const getItemProps = useCallback(
    index => {
      const row = Math.floor(index / columns) + 1;
      const col = (index % columns) + 1;

      return {
        tabIndex: index === focusIndex ? 0 : -1,
        onFocus: () => handleFocus(index),
        'data-nav-index': index,
        role: 'gridcell',
        'aria-rowindex': row,
        'aria-colindex': col,
      };
    },
    [focusIndex, handleFocus, columns],
  );

  const getContainerProps = useCallback(
    () => ({
      ref: containerRef,
      onKeyDown: handleKeyDown,
      role: 'grid',
      'aria-rowcount': rows,
      'aria-colcount': columns,
      'data-navigable-container': true,
    }),
    [handleKeyDown, rows, columns],
  );

  return {
    focusIndex,
    items,
    getItemProps,
    getContainerProps,
    focusItem,
  };
}
